import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Check, X, Edit2 } from 'lucide-react';

interface InlineEditableCellProps {
    value: string | number | null | undefined;
    onSave: (value: string | number) => void;
    type?: 'text' | 'number' | 'datetime-local';
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    displayValue?: string; // Optional formatted display value
}

const InlineEditableCell: React.FC<InlineEditableCellProps> = ({
    value,
    onSave,
    type = 'text',
    placeholder,
    disabled = false,
    className = '',
    displayValue,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value?.toString() || '');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            if (type !== 'datetime-local') {
                inputRef.current.select();
            }
        }
    }, [isEditing, type]);

    const handleStartEdit = () => {
        if (disabled) return;
        if (type === 'datetime-local' && value) {
            // Convert ISO string to datetime-local format (YYYY-MM-DDTHH:mm)
            const date = new Date(value.toString());
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            setEditValue(`${year}-${month}-${day}T${hours}:${minutes}`);
        } else {
            setEditValue(value?.toString() || '');
        }
        setIsEditing(true);
    };

    const handleSave = () => {
        let newValue: string | number;
        if (type === 'number') {
            newValue = parseFloat(editValue);
        } else if (type === 'datetime-local') {
            // Convert datetime-local format to ISO string
            newValue = editValue ? new Date(editValue).toISOString() : editValue;
        } else {
            newValue = editValue;
        }
        onSave(newValue);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditValue(value?.toString() || '');
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    };

    if (isEditing) {
        return (
            <div className={`flex items-center gap-1 ${className}`}>
                <Input
                    ref={inputRef}
                    type={type}
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    className="h-8 text-sm flex-1 min-w-[140px]"
                />
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleSave}
                    className="h-8 w-8 p-0 flex-shrink-0"
                >
                    <Check className="h-3 w-3" />
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleCancel}
                    className="h-8 w-8 p-0 flex-shrink-0"
                >
                    <X className="h-3 w-3" />
                </Button>
            </div>
        );
    }

    // Format datetime for display, or use provided displayValue
    const formattedDisplayValue =
        displayValue !== undefined
            ? displayValue
            : type === 'datetime-local' && value
              ? new Date(value.toString()).toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                })
              : (value ?? '');

    return (
        <div
            className={`flex items-center gap-1 group cursor-pointer ${className}`}
            onClick={handleStartEdit}
        >
            <span className="flex-1">{formattedDisplayValue}</span>
            {!disabled && (
                <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
            )}
        </div>
    );
};

export default InlineEditableCell;
